import React, { useState, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
} from '@mui/material';
import { useDrop } from 'react-dnd';
import ProductivityChat from '../../components/ProductivityChatWindows/FBGProductivityChat';
import TaskButton from '../../components/TaskButtons/TaskButton';
import figAgentLogo from '../../assets/fig_agent.png';
import figLogo from '../../assets/fig_CROP.png';
import { useAuth } from '../../context/AuthContext';
import usePersistentState from '../../hooks/usePersistentState';

const ItemType = 'task';

/* Force dark text on canvas tiles, regardless of gradient/status color */
const DARK_TEXT_ALWAYS_SX = {
    '& .MuiButton-root, & .MuiButton-root *': {
        color: '#0f172a !important',
    },
};

const Sandbox = () => {
    const { user } = useAuth();
    const tasksKey = `fig:canvas:${user?.id || 'anon'}`;

    const [droppedTasks, setDroppedTasks, clearCanvas] = usePersistentState(tasksKey, []);
    const dropRef = useRef(null);

    // React-DnD drop (from TaskButton)
    const [, drop] = useDrop({
        accept: ItemType,
        drop: (draggedItem) => {
            const newId = draggedItem.id || `task-${Date.now()}`;
            setDroppedTasks(prev => [
                ...prev,
                {
                    id: newId,
                    content: draggedItem.content,
                    // keep status/variant so user can change color; initial shows gradient
                    variant: 'figIce',
                    status: 'Initial',
                }
            ]);
            return { moved: true };
        },
    });

    // Native text drag/drop (select response in chat and drop here)
    const handleNativeDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    }, []);

    const handleNativeDrop = useCallback((e) => {
        e.preventDefault();
        const text = e.dataTransfer.getData('text/plain');
        if (text && text.trim()) {
            setDroppedTasks(prev => [
                ...prev,
                { id: `task-${Date.now()}`, content: text.trim(), variant: 'figIce', status: 'Initial' }
            ]);
        }
    }, [setDroppedTasks]);

    const handleDeleteTask = (taskId) => {
        setDroppedTasks(prev => prev.filter(t => t.id !== taskId));
    };

    const openRequestForm = () => {
        window.open(
            'https://app.smartsheet.com/b/form/3507048c22ba47c196575bfc27d8407a',
            '_blank',
            'width=800,height=800,top=100,left=100'
        );
    };

    const attachDropRefs = (el) => {
        drop(el);
        dropRef.current = el;
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f9f9f9' }}>
            {/* Instructions Section */}
            <Box
                sx={{
                    backgroundColor: '#eef4f7',
                    p: 3,
                    borderBottom: '2px solid #ccc',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: 2,
                }}
            >
                {/* Column 1 */}
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#111827', mb: 1 }}>
                        USDA Food Buying Guide Assistant
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#374151', mb: 1 }}>
                        The Fig Agent is purpose-built to support school nutrition professionals with deep expertise in all things K-12 Nutrition. Trained on the USDA Food Buying Guide (FBG) and aligned with federal and state regulations, the FIG Agent helps you confidently navigate meal planning, procurement, compliance, program financials, staffing, and USDA policy.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                        Ask for:
                        <ul style={{ margin: '4px 0 0 16px', padding: 0, textAlign: 'left' }}>
                            <li>Serving yields, purchase quantities, & conversion factors</li>
                            <li>Crediting information for meal-pattern compliance</li>
                            <li>Recipe scaling and cost projections</li>
                            <li>Suggested substitutions or product specs</li>
                            <li>Any other FBG-related guidance</li>
                        </ul>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Button variant="contained" color="primary" onClick={openRequestForm}>
                            Submit a Request Form
                        </Button>
                        {/* Solid so it stands out */}
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={clearCanvas}
                            sx={{ textTransform: 'none' }}
                        >
                            Clear Canvas
                        </Button>
                    </Box>
                </Box>

                {/* Column 2: Example Request Card (kept light/plain) */}
                <Box>
                    <Paper elevation={1} sx={{ p: 2, backgroundColor: '#ffffff', borderRadius: 8, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#111827' }}>
                            Example Request:
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: '#1f2937' }}>
                            {`I'm planning a school-lunch entrée for 200 K-8 students, I will serve this meal 2 times per moth at 9 schools.  that meets the NSLP meal pattern for a 2-ounce-eq meat/meat-alternate.
• Recommend a chicken-based recipe drawn from the Food Buying Guide (FBG).
• For each ingredient, list: purchase quantity (AP), servings/yield (EP), crediting information, and the FBG reference (section & item).
• Show a cost projection using current FBG price estimates.
• Identify at least one vegetarian substitution that would provide the same crediting.
• Explain any conversion factors you used.  I will need to order this the componets `}
                        </Typography>
                    </Paper>
                </Box>
            </Box>

            {/* Main Content (Chat + Drop Area) */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Left: Chat */}
                <Box sx={{ width: '35%', p: 3, backgroundColor: '#eef2f5', borderRight: '2px solid #e5e7eb', overflowY: 'auto' }}>
                    <ProductivityChat />
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#374151' }}>
                        Tip: Select any AI response text and drag it into the canvas on the right.
                    </Typography>
                </Box>

                {/* Right: Drop Zone (Canvas) */}
                <Box
                    ref={attachDropRefs}
                    onDragOver={handleNativeDragOver}
                    onDrop={handleNativeDrop}
                    sx={{
                        flex: 1,
                        p: 3,
                        backgroundColor: '#ffffff',
                        overflowY: 'auto',
                        position: 'relative',
                        borderLeft: '2px solid #e5e7eb'
                    }}
                >
                    {/* Watermark Logos */}
                    <Box
                        component="img"
                        src={figAgentLogo}
                        alt="FIG Agent Logo Watermark"
                        sx={{
                            position: 'absolute',
                            width: 400,
                            height: 400,
                            opacity: 0.08,
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                        }}
                    />
                    <Box
                        component="img"
                        src={figLogo}
                        alt="FIG Logo"
                        sx={{ position: 'absolute', width: 300, height: 100, opacity: 0.04, bottom: 16, right: 16, pointerEvents: 'none' }}
                    />

                    {/* Empty-state helper */}
                    {droppedTasks.length === 0 && (
                        <Box
                            sx={{
                                border: '2px dashed #bcdbe3',
                                borderRadius: 8,
                                p: 3,
                                my: 2,
                                color: '#5b7a83',
                                textAlign: 'center',
                            }}
                        >
                            Drag AI responses or task tiles here to save them as cards.
                        </Box>
                    )}

                    {/* Dropped Tasks */}
                    {droppedTasks.map((task) => (
                        <Box
                            key={task.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                border: '1px solid #e6eef0',
                                borderRadius: 8,
                                p: 1,
                                mb: 1.25,
                                backgroundColor: '#ffffff',
                            }}
                        >
                            {/* Use TaskButton's gradient (figIce) while uncategorized; force dark text always */}
                            <Box sx={DARK_TEXT_ALWAYS_SX}>
                                <TaskButton
                                    id={task.id}
                                    content={task.content}
                                    initialStatus={task.status}   // "Initial" → gradient; status menu can change colors
                                    variant="figIce"
                                />
                            </Box>

                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteTask(task.id)}
                            >
                                Delete
                            </Button>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default Sandbox;
